/**
 * Soft≠Live Response helpers — normalize Live dispatch / roster / rules only.
 * Soft invent is forbidden — never invent tickets, candidates, consent rows, or cell roles.
 */
import { formatUserInstant } from "./time.js";

const SEAT_COPY =
  "Your account cannot use this Response seat. Ask a manager if you need access.";
const SITE_COPY =
  "This account is not on a Live site yet. Ask a manager to add you.";
const GENERIC_COPY = "That action could not be completed.";

/** Classify Live Response errors — site/csrf ≠ seat sealed. */
export function softResponseErrorKind(err) {
  const low = String(err == null ? "" : err).trim().toLowerCase();
  if (!low) return "empty";
  if (/csrf/.test(low)) return "csrf";
  if (/site_membership_required|site_scope_denied|site_scope_ambiguous|default_site_ambiguous|default_site_missing/.test(low)) {
    return "site";
  }
  if (/site_authority_sa_not_exposed/.test(low)) return "sa_site";
  if (/dispatch_site_scope_required|dispatch_resource_site_unknown|dispatch_site_unresolved/.test(low)) {
    return "dispatch_site";
  }
  if (/location_consent_required/.test(low)) return "consent";
  if (/tenant_scope_required|board_team_requires_owner_admin/.test(low)) return "tenant";
  if (/_admin_only|seat_gated|user_floor|team_admin|staff_read|insufficient role/.test(low)) {
    return "seat";
  }
  if (/\b401\b|session ended|unauthorized|missing bearer/.test(low)) return "auth";
  if (/\b429\b|too many/.test(low)) return "rate";
  if (/network|failed to fetch|timeout/.test(low)) return "network";
  if (/\b403\b/.test(low)) return "refused";
  return "generic";
}

/** Path-free operator copy for Response desks. Never surface GET/POST paths. */
export function softResponseErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return GENERIC_COPY;
  const kind = softResponseErrorKind(n);
  if (kind === "csrf") {
    return "This page needs a fresh security token. Refresh, then try again.";
  }
  if (kind === "site") return SITE_COPY;
  if (kind === "sa_site") {
    return "Platform staff are not site members. Use a site manager seat.";
  }
  if (kind === "dispatch_site") {
    return "This send is not bound to a Live site yet.";
  }
  if (kind === "consent") {
    return "People need location consent before they can be ranked.";
  }
  if (low === "no_fresh_phone_locations") {
    return "No on-call people with a fresh phone location in this site.";
  }
  /* Live posture detail keys (status SoT) → human copy. */
  if (low === "response_shell_act_user_floor") {
    return "You can open queues, but create or change actions need a manager seat.";
  }
  if (low === "board_team_admin_only_use_mine") {
    return "Team board is for managers. Open My queue for your own assignments.";
  }
  if (kind === "tenant" || low === "board_team_requires_owner_admin" || /tenant_scope_required/.test(low)) {
    return "Platform staff must select a tenant owner before opening the team board. Your own assignments stay on My queue.";
  }
  if (low === "assign_preview_admin_only" || low === "assign_create_admin_only") {
    return "Assigning responders needs a site manager seat.";
  }
  if (low === "rules_admin_only") {
    return "Dispatch rules are manager-only.";
  }
  if (low === "automations_admin_only") {
    return "Automations are manager-only.";
  }
  if (low === "cell_admin_only") {
    return "Incident cell control is manager-only.";
  }
  if (low === "cases_command_admin_only") {
    return "Case command actions need a site manager seat.";
  }
  if (low === "drill_admin_only") {
    return "Practice and live drills need a manager seat.";
  }
  if (low === "readiness_admin_only") {
    return "Readiness score is manager-only.";
  }
  if (low === "playbook_admin_only") {
    return "Playbook tips are manager-only.";
  }
  if (low === "equipment_admin_only") {
    return "Equipment practice is manager-only.";
  }
  if (low === "robots_admin_only") {
    return "External robots and SEB need a manager seat.";
  }
  if (kind === "seat") return SEAT_COPY;
  if (kind === "auth" || /\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|unknown operation/.test(low)) {
    return "This Response seat is not available right now.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  /* Hybrid flag OFF / runtime gate — never invent Soft success as Live send. */
  if (/response_hybrid_disabled|response_hybrid_not_ready/.test(low)) {
    return "Hybrid Live send is not armed on this site yet.";
  }
  if (/phone_locations_history_disabled|history_disabled|track.*not armed/.test(low)) {
    return "Handset history is not armed on this deployment — track stays dark.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Response services are not ready right now.";
  }
  if (kind === "network") {
    return "Network issue — try again in a moment.";
  }
  if (/\b422\b|\b409\b|invalid|required|conflict/.test(low)) {
    return "That change was not accepted.";
  }
  if (kind === "refused") {
    return "This Response read was refused.";
  }
  if (/\/api\/|\/response\/|\/responder|\/personnel|\/dispatch|\/incidents|\/automations|\/sop|\/actuators|\/readiness|http|get |post |patch |status_path|page\d+|soft invent|sot/.test(low)) {
    return GENERIC_COPY;
  }
  /* snake_case residual detail → generic (never paint backend keys as seat-seal). */
  if (/^[a-z][a-z0-9_]{3,}$/.test(n) && n.includes("_")) {
    return GENERIC_COPY;
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** @param {unknown} envelope GET /responder-dispatches */
export function dispatchRowsOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.records || (Array.isArray(root) ? root : []);
  if (!Array.isArray(items)) return [];
  return items
    .map((r) => {
      if (!r || typeof r !== "object") return null;
      const id = String(r.dispatch_id || r.id || "").trim();
      if (!id) return null;
      const status = String(r.status || "pending").trim() || "pending";
      const risk = Number(r.risk_score);
      const opened = String(r.created_at || r.accepted_at || "").trim();
      return {
        id,
        status,
        who: String(r.assigned_responder || r.responder_username || "").trim() || "—",
        dev: String(r.device_id || "").trim() || "—",
        devLabel: String(r.device_label || r.device_id || "").trim() || "—",
        reason: String(r.dispatch_reason || r.reason || "").trim() || "—",
        risk: Number.isFinite(risk) ? risk : 0,
        km: "—",
        opened: opened ? formatUserInstant(opened, { seconds: false }) : "—",
        correlation: String(r.correlation_id || r.alarm_id || "").trim() || "—",
        incident: String(r.incident_id || r.case_id || "").trim(),
        resolution: r.resolution != null ? String(r.resolution) : "",
        raw: r,
      };
    })
    .filter(Boolean);
}

/**
 * @param {unknown} envelope POST /responder-dispatches/preview
 * @param {string} [deviceId]
 */
export function candidateRowsOf(envelope, deviceId) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const rows = root.candidates || root.items || [];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((c, i) => {
      if (!c || typeof c !== "object") return null;
      const id = String(c.username || c.user_id || c.id || "").trim();
      if (!id) return null;
      const km = Number(c.distance_km ?? c.km ?? c.distance_m / 1000);
      const eligible = c.eligible !== false
        && !c.busy
        && c.on_call !== false
        && c.stale !== true;
      const kindRaw = String(c.kind || c.unit_kind || c.asset_class || "human").toLowerCase();
      const kind = kindRaw === "drone" || kindRaw === "uav"
        ? "drone"
        : (kindRaw === "robot" || kindRaw === "ugv" || kindRaw === "bot" ? "robot" : "human");
      return {
        id,
        label: String(c.display_name || c.label || id).trim() || id,
        kind,
        rank: i + 1,
        km: Number.isFinite(km) ? Math.round(km * 10) / 10 : "—",
        eligible: !!eligible && c.rank_conflict !== true && c.needs_you !== true,
        on: c.on_call !== false,
        busy: !!c.busy,
        stale: !!c.stale,
        rankConflict: c.rank_conflict === true || c.needs_you === true,
        needsYou: c.needs_you === true || c.rank_conflict === true,
        zonePriority: Number(c.zone_priority || 0) || 0,
        reason: String(c.reason || c.why || (eligible ? "ranked" : "not eligible")).trim(),
        why: (c.rank_conflict === true || c.needs_you === true)
          ? String(c.why || "overlapping Zone Rank policies disagree — Needs You").trim()
          : (eligible ? "" : String(c.why || "").trim()),
        deviceId: String(deviceId || c.device_id || "").trim(),
        raw: c,
      };
    })
    .filter(Boolean);
}

/**
 * Pure: Live map/register labels from one GET /personnel row.
 * Soft≠Live: never invent display names — only server-provided fields.
 */
export function personnelLiveMapRowOf(row) {
  if (!row || typeof row !== "object") return null;
  const user = String(row.username || "").trim();
  if (!user) return null;
  const display = String(row.display_name || row.name || row.label || user).trim() || user;
  return {
    id: user,
    name: display,
    callsign: String(row.callsign || "").trim(),
    site: String(row.site_label || row.site || "").trim(),
    crew: String(row.crew || row.team || "").trim(),
    registerProfileLive: row.register_profile_live === true,
  };
}

/** Pure: datetime-local or ISO → UTC ISO for track query (invalid → omit). */
export function personnelTrackIsoOf(localOrIso) {
  const s = String(localOrIso || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !s.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

/** Pure: build GET /personnel/{u}/track query — only server-known keys. */
export function personnelTrackQueryOf({ limit, since, until } = {}) {
  const q = {};
  const lim = Number(limit);
  if (Number.isFinite(lim) && lim > 0) q.limit = Math.floor(lim);
  const sinceIso = personnelTrackIsoOf(since);
  const untilIso = personnelTrackIsoOf(until);
  if (sinceIso) q.since = sinceIso;
  if (untilIso) q.until = untilIso;
  return q;
}

/** @param {unknown} envelope GET /personnel */
export function personnelRosterOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || [];
  if (!Array.isArray(items)) return [];
  return items
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const user = String(p.username || "").trim();
      if (!user) return null;
      const detail = p.consent_detail && typeof p.consent_detail === "object" ? p.consent_detail : {};
      const consent = String(p.consent || detail.state || "none").trim();
      const active = consent === "granted" || consent === "active";
      const on = p.on_call === true || (p.on_call && p.on_call.on_call === true);
      const granted = String(detail.granted_at || "").trim();
      const phone = String(p.phone || "").trim();
      const region = String(p.phone_region || "").trim();
      const pos = p.position && typeof p.position === "object" ? p.position : null;
      const lat = pos != null ? Number(pos.lat ?? pos.latitude) : NaN;
      const lng = pos != null ? Number(pos.lng ?? pos.longitude) : NaN;
      const assign = p.assignment && typeof p.assignment === "object" ? p.assignment : null;
      const display = String(p.display_name || p.label || p.name || user).trim() || user;
      const siteLabel = String(p.site_label || p.site || "").trim();
      return {
        user,
        name: display,
        role: String(p.role || "").trim() || "—",
        site: siteLabel || String(p.tenant || detail.site_id || "—").trim() || "—",
        phone: phone || "",
        phoneRegion: region || "",
        phoneSay: phone ? (region ? `${phone} · ${region}` : phone) : "No phone on file",
        ver: String(detail.consent_version || "—").trim() || "—",
        granted: granted ? formatUserInstant(granted, { seconds: false }) : "—",
        ret: Number(detail.retention_days) || "—",
        tier: String(detail.accuracy_tier || "—").trim() || "—",
        status: active ? "active" : (consent === "revoked" ? "revoked" : "none"),
        on: !!on,
        hasFix: Number.isFinite(lat) && Number.isFinite(lng),
        positionSay: Number.isFinite(lat) && Number.isFinite(lng)
          ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
          : "No live fix",
        assignmentSay: assign
          ? String(assign.dispatch_id || assign.device_id || assign.status || "on a job").trim()
          : "Not on a Live job",
        soft_demo: p.soft_demo === true,
        crew: String(p.crew || p.team || "").trim(),
        callsign: String(p.callsign || "").trim(),
        notes: String(p.notes || "").trim(),
        registerProfileLive: p.register_profile_live === true,
        raw: p,
      };
    })
    .filter(Boolean);
}

/** @param {unknown} envelope GET /dispatch-rules */
export function dispatchRulesOf(envelope) {
  const root = envelope && typeof envelope === "object" ? envelope : {};
  const items = root.items || root.rules || [];
  if (!Array.isArray(items)) return [];
  return items
    .map((r) => {
      if (!r || typeof r !== "object") return null;
      const id = String(r.rule_id || r.id || "").trim();
      if (!id) return null;
      const thr = Number(r.threshold_value ?? r.threshold);
      const certAt = r.certified_at;
      let certified = null;
      if (certAt != null && String(certAt).trim()) {
        const n = Number(certAt);
        if (Number.isFinite(n) && n > 1e11) {
          certified = formatUserInstant(n, { seconds: false });
        } else if (Number.isFinite(n) && n > 1e9) {
          certified = formatUserInstant(n, { seconds: false });
        } else {
          certified = formatUserInstant(certAt, { seconds: false });
        }
      }
      return {
        id,
        name: String(r.name || id).trim() || id,
        threshold: Number.isFinite(thr) ? thr : 0,
        obs: String(r.source_observation_type || "").trim(),
        metric: String(r.metric_name || "").trim(),
        op: String(r.operator || "").trim(),
        kind: String(r.resource_kind || "").trim(),
        action: String(r.action_type || r.action || "").trim() || "—",
        prio: Number(r.priority) || 0,
        rev: Number(r.revision) || 1,
        /* Fail-closed paint: rule is on only when Live says true. */
        enabled: r.enabled === true,
        certified,
        drift: false,
        reason: String(r.revision_note || "").trim() || null,
        raw: r,
      };
    })
    .filter(Boolean);
}

/** Map Live-sourced candidate rows → Live import body (no Soft brochure invent). */
export function candidatePlanImportBody(plan) {
  const rules = (Array.isArray(plan) ? plan : []).map((c) => ({
    rule_id: String(c.id || "").trim() || undefined,
    name: String(c.name || "").trim(),
    source_observation_type: c.obs || "alarm",
    metric_name: c.metric || "risk_score",
    operator: c.op || "gte",
    threshold_value: Number(c.threshold) || 0,
    resource_kind: c.kind || "human_responder",
    action_type: c.action === "dispatch" ? "device_cmd" : (c.action || "device_cmd"),
    priority: Number(c.prio) || 0,
    enabled: true,
    revision_note: "CMC Response · Live-sourced plan import",
  })).filter((r) => r.name);
  return { rules };
}

/** Snapshot Live rule rows into an importable draft (never Soft invent). */
export function draftPlanFromLiveRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => {
      if (!r || typeof r !== "object") return null;
      const id = String(r.id || "").trim();
      const name = String(r.name || "").trim();
      if (!id && !name) return null;
      return {
        id: id || name,
        name: name || id,
        threshold: Number(r.threshold) || 0,
        obs: String(r.obs || "").trim() || "alarm",
        metric: String(r.metric || "").trim() || "risk_score",
        op: String(r.op || "").trim() || "gte",
        kind: String(r.kind || "").trim() || "human_responder",
        action: String(r.action || "").trim() || "dispatch",
        prio: Number(r.prio) || 0,
      };
    })
    .filter(Boolean);
}
